import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import Status from "../../enums/Status";
import IDSAR from "../../models/dsar/IDSAR";
import dayjs from "dayjs";
import { Card, Col, Container, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import sarService from "../../services/sarService";
import { Icon } from "../../components/icon/Icon";
import AlertBody from "../../components/alertBody/AlertBody";
import RequestBody from "./body/RequestBody";
import ApprovalButtons from "./ApprovalButtons";
import { useMsal } from "@azure/msal-react";
import Appeal from "./Appeal";

export function Request() {
    const { id } = useParams();
    const { t } = useTranslation();
    const { accounts } = useMsal();
    
    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [request, setRequest] = useState<IDSAR>(null!);
    const [isAuthoriser, setIsAuthoriser] = useState<boolean>(false);

    const account = accounts[0];
    const username = account?.username;

    useEffect(() => {
        fetchData(id!, username, setRequest, setIsAuthoriser, setStatus);
    }, [id, username]);

    const createdFormatted = request?.created
        ? dayjs(request!.created).format("DD/MM/YYYY HH:mm")
        : null;
    
    return (
        <Container className="py-3">
            <Row className="justify-content-center">
                <Col>
                    <Card className="shadow">
                        {
                            (status === Status.LOADING && <AlertBody variant="info" message={t("dsar.loading")} backButtonPath="/sars" />) ||
                            (status === Status.LOAD_ERROR && <AlertBody variant="danger" message={t("dsar.errors.general")} backButtonPath="/sars" />) ||
                            (status === Status.LOAD_SUCCESS && <RequestBody request={request} />)
                        }
                        {
                            // Request is in queue - no authoriser; hides if the requestor is the same as the current user
                            status === Status.LOAD_SUCCESS &&
                            request.status === "In Queue" &&
                            request.requestor?.email !== username &&
                            <ApprovalButtons requestId={request.id} appealReason={request.appealNotes} />
                        }
                        {
                            // Request is under review - shown only when the approver is the same as the current user
                            status === Status.LOAD_SUCCESS &&
                            request.status === "Under Review" &&
                            isAuthoriser &&
                            <ApprovalButtons requestId={request.id} appealReason={request.appealNotes} />
                        }
                        {
                            // Request is awaiting appeal - shown only when the requestor is the same as the current user
                            status === Status.LOAD_SUCCESS &&
                            request.status === "Requestor Review" &&
                            request.requestor?.email === username &&
                            <Appeal requestId={request.id} rejectionReason={request.rejectionReason!} />
                        }
                        {
                            status === Status.LOAD_SUCCESS && createdFormatted &&
                            <Card.Footer>
                                <div className="d-flex justify-content-between fs-sm">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("general.created")}</Tooltip>}>
                                        <span>
                                            <Icon iconName="Calendar" endMargin={1} />
                                            <span className="mb-0">{createdFormatted}</span>
                                        </span>
                                    </OverlayTrigger>
                                </div>
                            </Card.Footer>
                        }
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

async function fetchData(
    id: string,
    username: string,
    setRequest: (request: IDSAR) => void,
    setIsAuthoriser: (isAuthoriser: boolean) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const authorisers = await sarService.getAuthorisers();

        const request = await sarService.getSAR(id);
        setRequest(request);
        
        const isAuthoriser = !!authorisers.find(a => a.email === username);
        setIsAuthoriser(isAuthoriser);

        setStatus(Status.LOAD_SUCCESS);
    } catch {
        setStatus(Status.LOAD_ERROR);
    }
}